/**
 * Summary Prompts Service
 * Manages GPT prompt templates for summary generation
 */

import { getApiUrl } from '../utils/apiUtils';

export interface PromptTemplate {
  prompt_type: 'teacher' | 'reader';
  grade_level: 'K-2' | '3-5' | '6-8' | '9-12' | 'all';
  prompt_template: string;
  is_default: boolean;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface PromptVariable {
  variable_name: string;
  description: string;
  example_value: string;
  data_type: string;
  is_required: boolean;
}

export interface UpdatePromptRequest {
  prompt_template: string;
  notes?: string;
}

/**
 * Get all prompt templates with optional filters
 */
export async function listPrompts(
  promptType?: 'teacher' | 'reader',
  gradeLevel?: string,
  includeInactive?: boolean
): Promise<PromptTemplate[]> {
  const params = new URLSearchParams();
  if (promptType) params.append('prompt_type', promptType);
  if (gradeLevel) params.append('grade_level', gradeLevel);
  if (includeInactive) params.append('include_inactive', 'true');

  const response = await fetch(getApiUrl(`/v1/dashboard/summary-prompts?${params}`));
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to list prompts');
  }
  
  return response.json();
}

/**
 * Get specific prompt template
 */
export async function getPrompt(
  promptType: 'teacher' | 'reader',
  gradeLevel: string
): Promise<PromptTemplate> {
  const response = await fetch(
    getApiUrl(`/v1/dashboard/summary-prompts/${promptType}/${gradeLevel}`)
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get prompt');
  }
  
  return response.json();
}

/**
 * Update prompt template (creates new version)
 */
export async function updatePrompt(
  promptType: 'teacher' | 'reader',
  gradeLevel: string,
  request: UpdatePromptRequest
): Promise<PromptTemplate> {
  const response = await fetch(
    getApiUrl(`/v1/dashboard/summary-prompts/${promptType}/${gradeLevel}`),
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update prompt');
  }
  
  return response.json();
}

/**
 * Reset prompt to default template
 */
export async function resetPrompt(
  promptType: 'teacher' | 'reader',
  gradeLevel: string
): Promise<PromptTemplate> {
  const response = await fetch(
    getApiUrl(`/v1/dashboard/summary-prompts/${promptType}/${gradeLevel}/reset`),
    {
      method: 'POST',
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to reset prompt');
  }
  
  return response.json();
}

/**
 * Get available variables for prompt type
 */
export async function getPromptVariables(
  promptType: 'teacher' | 'reader'
): Promise<PromptVariable[]> {
  const response = await fetch(
    getApiUrl(`/v1/dashboard/summary-prompts/variables/${promptType}`)
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get variables');
  }
  
  return response.json();
}

/**
 * Validate prompt template for required variables
 */
export function validatePromptTemplate(
  template: string,
  requiredVariables: string[]
): { isValid: boolean; missingVariables: string[] } {
  const missingVariables = requiredVariables.filter(
    variable => !template.includes(`{${variable}}`)
  );
  
  return {
    isValid: missingVariables.length === 0,
    missingVariables,
  };
}

/**
 * Extract variables used in template
 */
export function extractVariables(template: string): string[] {
  const regex = /\{(\w+)\}/g;
  const variables: string[] = [];
  let match;
  
  while ((match = regex.exec(template)) !== null) {
    variables.push(match[1]);
  }
  
  return variables;
}
